import * as React from 'react';
import { type AxiosResponse } from 'axios';
import { useTiersService, useUtilisateurService, useSociete } from '@europrocurement/l2d-domain';
import { type TiersTiersPrescripteurReadCommerciaux } from '@europrocurement/l2d-domain/openApi/ApiTiers';
import { type UtilisateursJsonldUtilisateurRead } from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';
import { type UserCardUserType } from '../UserContactCard';

export interface UseAssistanceContactsResult {
    formalisteUserData: UserCardUserType | null;
    commercialUserData: UserCardUserType | null;
    isLoadingContacts: boolean;
}

const flattenUserData = (user: UtilisateursJsonldUtilisateurRead): UserCardUserType => ({
    firstName: user.prenom,
    lastName: user.nom,
    mail: user.mail ?? undefined,
    phone: user.ligneDirecte ?? undefined,
    mobilePhone: user.portable ?? undefined,
    userIdentifier: user.userIdentifier ?? undefined,
    serviceLibelle: user.service?.libelle ?? undefined,
    poste: user.poste ?? undefined,
});

export const useAssistanceContacts = (
    prescripteur: { formaliste?: { id?: number }; id?: number } | null | undefined,
): UseAssistanceContactsResult => {
    const { utilisateurApi } = useUtilisateurService();
    const { prescribersApi } = useTiersService();
    const xIdSociete = useSociete();

    const [formalisteUserData, setFormalisteUserData] = React.useState<UserCardUserType | null>(
        null,
    );
    const [commercialUserData, setCommercialUserData] = React.useState<UserCardUserType | null>(
        null,
    );
    const [isLoadingContacts, setIsLoadingContacts] = React.useState(false);

    React.useEffect(() => {
        const loadContactsInfo = async () => {
            if (!prescripteur || !prescripteur.id || !xIdSociete) {
                return;
            }

            setIsLoadingContacts(true);

            try {
                const formalisteId = prescripteur.formaliste?.id;
                if (formalisteId) {
                    const formalisteResponse = await utilisateurApi.apiUtilisateursIdGet({
                        id: formalisteId.toString(),
                    });
                    setFormalisteUserData(flattenUserData(formalisteResponse.data));
                }

                const response: AxiosResponse<TiersTiersPrescripteurReadCommerciaux> =
                    await prescribersApi.getCommerciauxPrescripteurTiersItem({
                        id: prescripteur.id.toString(),
                        xIdSociete,
                    });

                const commercialUsers = response?.data?.commerciaux;

                if (commercialUsers && commercialUsers.length > 0) {
                    const matchingCommercial = commercialUsers.find(
                        (commercial) => commercial.actif && commercial.domaine?.id === 3,
                    );

                    if (matchingCommercial) {
                        const commercialInfo: UserCardUserType = {
                            firstName: matchingCommercial.utilisateur?.prenom ?? '',
                            lastName: matchingCommercial.utilisateur?.nom ?? '',
                            userIdentifier:
                                matchingCommercial.utilisateur?.userIdentifier ?? undefined,
                            mail: matchingCommercial.utilisateur?.email ?? undefined,
                            serviceLibelle: 'Commercial',
                        };
                        setCommercialUserData(commercialInfo);
                    } else {
                        setCommercialUserData({ firstName: '', lastName: '', mail: undefined });
                    }
                } else {
                    setCommercialUserData({ firstName: '', lastName: '', mail: undefined });
                }
            } catch (error) {
                console.error('Erreur lors du chargement des informations de contact:', error);
                setCommercialUserData({ firstName: '', lastName: '', mail: undefined });
            } finally {
                setIsLoadingContacts(false);
            }
        };

        loadContactsInfo();
    }, [prescripteur, prescribersApi, utilisateurApi, xIdSociete]);

    return {
        formalisteUserData,
        commercialUserData,
        isLoadingContacts,
    };
};
